import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { GridColDef } from "@mui/x-data-grid";
import { useState } from "react";

import { api, errorMessage } from "../api/client";
import { DataTable, DateText, PageHeader, StatusChip, useListParams, type ListParams } from "../components";
import help from "./HELP.md?raw";

type Reminder = { id: string; kind: string; title: string; url: string; due_at: string; offsets_hours: number[]; sent_offsets: number[]; status: string; target_type: string; object_id: string | null; created_at: string };

function useReminders(params: ListParams) {
  return useQuery({ queryKey: ["reminders", params], queryFn: async () => { const { data, error } = await api.GET("/api/v1/workflow/reminders/", { params: { query: params as never } }); if (error) throw new Error(errorMessage(error)); return data as unknown as { count: number; results: Reminder[] }; } });
}

export function Reminders() {
  const qc = useQueryClient();
  const [params, setParams] = useListParams({ ordering: "due_at", status: "scheduled" });
  const list = useReminders(params);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", due_at: "", url: "" });
  const create = useMutation({
    mutationFn: async () => { const { error } = await api.POST("/api/v1/workflow/reminders/", { body: { kind: "task", title: form.title, due_at: new Date(form.due_at).toISOString(), url: form.url, offsets_hours: [24, 0] } as never }); if (error) throw new Error(errorMessage(error)); },
    onSuccess: () => { setCreating(false); void qc.invalidateQueries({ queryKey: ["reminders"] }); },
  });
  const done = useMutation({
    mutationFn: async (id: string) => { await api.PATCH("/api/v1/workflow/reminders/{id}/", { params: { path: { id } }, body: { status: "done" } as never }); },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["reminders"] }),
  });
  const columns: GridColDef<Reminder>[] = [
    { field: "title", headerName: "Reminder", flex: 1 },
    { field: "kind", headerName: "Kind", width: 100 },
    { field: "due_at", headerName: "Due", width: 160, renderCell: (p) => <DateText value={p.value as string} withTime /> },
    { field: "status", headerName: "Status", width: 110, renderCell: (p) => <StatusChip status={p.value as string} /> },
    { field: "actions", headerName: "", width: 120, sortable: false, renderCell: (p) => p.row.status === "scheduled" ? <Button size="small" onClick={() => done.mutate(p.row.id)}>Mark done</Button> : null },
  ];
  return (
    <>
      <PageHeader help={help} title="Reminders & tasks" crumbs={[{ label: "Reminders" }]} actions={<Button variant="contained" onClick={() => setCreating(true)}>New reminder</Button>} />
      <DataTable viewKey="workflow.reminders" columns={columns} data={list.data} isLoading={list.isLoading} params={params} onParamsChange={setParams}
        filters={<TextField select size="small" label="Status" value={String(params.status ?? "")} onChange={(e) => setParams({ ...params, status: e.target.value, page: 1 })} slotProps={{ select: { native: true } }} sx={{ width: 150 }}><option value="">All</option><option value="scheduled">Scheduled</option><option value="done">Done</option><option value="canceled">Canceled</option></TextField>} />
      <Dialog open={creating} onClose={() => setCreating(false)}>
        <DialogTitle>New reminder</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} autoFocus />
          <TextField type="datetime-local" label="Due" InputLabelProps={{ shrink: true }} value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} />
          <TextField label="Link (optional, e.g. /app/sales/orders/…)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setCreating(false)}>Cancel</Button><Button variant="contained" disabled={!form.title || !form.due_at || create.isPending} onClick={() => create.mutate()}>Save</Button></DialogActions>
      </Dialog>
    </>
  );
}
